import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ROW_DROP_MAX_STAGGER, ROW_DROP_MS, ROW_DROP_STAGGER_MS } from './ProcessTreeRow'

/**
 * useTreeFolding — how the rows of a set come and go when its arrow is pressed, the SAME in the Tree and in the tree of
 * the graph's panel (story 167, PO 29/09/2026). Opening, the rows the set now shows fall into place one after the other
 * (the drop of the Tree, PO review 2026-09-21); closing, they rise and fade the other way round, last one first, and
 * only then leave the list.
 *
 * The rows of a set are the ones right under it that are deeper than it — not the ones whose key starts with its key:
 * the operation that follows a set in a sequence also builds its key on the key of the set.
 */
const NO_FOLDING = { key: '', rows: [] }

export const rowsInsideOf = (setRow, shownRows) => {
  const start = shownRows.findIndex((row) => row.key === setRow.key)
  if (start < 0) {
    return []
  }
  const inside = []
  for (let index = start + 1; index < shownRows.length && (shownRows[index].depth || 0) > (setRow.depth || 0); index++) {
    inside.push(shownRows[index])
  }
  return inside
}

/** How long the last row of a fold takes to leave: its delay in the cascade plus its own movement. */
export const foldDurationOf = (count) => Math.min(Math.max(count - 1, 0), ROW_DROP_MAX_STAGGER) * ROW_DROP_STAGGER_MS + ROW_DROP_MS

export const useTreeFolding = (visibleRows) => {
  const [enteringKey, setEnteringKey] = useState('')
  const [folding, setFolding] = useState(NO_FOLDING)
  const shownRowsRef = useRef(visibleRows)
  shownRowsRef.current = visibleRows
  const timerRef = useRef(0)
  useEffect(() => () => window.clearTimeout(timerRef.current), [])

  const setOpened = useCallback((setRow) => {
    window.clearTimeout(timerRef.current)
    setFolding(NO_FOLDING)
    setEnteringKey(setRow.key)
  }, [])

  const setClosed = useCallback((setRow) => {
    window.clearTimeout(timerRef.current)
    const leaving = rowsInsideOf(setRow, shownRowsRef.current)
    setEnteringKey('')
    setFolding({ key: setRow.key, rows: leaving })
    timerRef.current = window.setTimeout(() => setFolding(NO_FOLDING), foldDurationOf(leaving.length))
  }, [])

  const leaveIndexByKey = useMemo(() => {
    const indexes = new Map()
    folding.rows.forEach((row, index) => indexes.set(row.key, folding.rows.length - 1 - index))
    return indexes
  }, [folding])

  // What is on screen: the visible rows and, while a set folds, the rows it was showing, back under it.
  const displayedRows = useMemo(() => {
    const setIndex = visibleRows.findIndex((row) => row.key === folding.key)
    if (setIndex < 0 || folding.rows.length === 0 || visibleRows.some((row) => leaveIndexByKey.has(row.key))) {
      return visibleRows
    }
    return [...visibleRows.slice(0, setIndex + 1), ...folding.rows, ...visibleRows.slice(setIndex + 1)]
  }, [visibleRows, folding, leaveIndexByKey])

  const enterIndexByKey = useMemo(() => {
    const indexes = new Map()
    const opened = enteringKey ? visibleRows.find((row) => row.key === enteringKey) : null
    if (opened) {
      rowsInsideOf(opened, visibleRows).forEach((row, index) => indexes.set(row.key, index))
    }
    return indexes
  }, [enteringKey, visibleRows])

  return { displayedRows, enterIndexByKey, leaveIndexByKey, setOpened, setClosed }
}
